import { IntermediateNodeWithConditionalsAndOutputs } from 'Automations/nodeDefinitions/helpers/presentationToConductorWorkflow'
import { SemanticType } from '../../types'

/**
 * Thrown by `getClosestMatches` when one or more of a node's required inputs
 * cannot be resolved from its upstream nodes. Carries the full list of
 * unresolved input properties so validation can surface every missing input at
 * once (rather than failing on the first). Generation still fails on a missing
 * input exactly as before — only the error type is richer.
 */
export class MissingUpstreamDataError extends Error {
  readonly missingProperties: string[]

  constructor(missingProperties: string[]) {
    super(`Missing upstream data: ${missingProperties.join(', ')}`)
    this.name = 'MissingUpstreamDataError'
    this.missingProperties = missingProperties
  }
}

export const getClosestMatch = ({
  semanticType,
  upstreamNodes
}: {
  semanticType: SemanticType
  upstreamNodes: IntermediateNodeWithConditionalsAndOutputs[]
}): string => {
  /**
   * `upstreamNodes` are ordered beginning with the start node
   * Reversing the order causes `find` to return the semantic value from the node that is closest to the current node
   * which would come after the last node in `upstreamNodes`
   */
  const closestNode = [...upstreamNodes].reverse().find(node => {
    return Object.values(node.outputs).some(
      output => output.semanticType === semanticType
    )
  })

  if (!closestNode) {
    throw new Error('No upstream node found with matching semantic type')
  }

  const matchingOutput = Object.values(closestNode.outputs).find(
    output => output.semanticType === semanticType
  )

  if (!matchingOutput) {
    throw new Error('Semantic type not found on node outputs')
  }

  return matchingOutput.id
}

export const getClosestMatches =
  <Inputs extends string>(
    upstreamNodes: IntermediateNodeWithConditionalsAndOutputs[]
  ) =>
  (inputs: Record<Inputs, SemanticType>): Record<Inputs, string> => {
    const { resolved, missing } = (Object.keys(inputs) as Inputs[]).reduce<{
      resolved: Record<Inputs, string>
      missing: Inputs[]
    }>(
      (acc, curr) => {
        try {
          return {
            ...acc,
            resolved: {
              ...acc.resolved,
              [curr]: getClosestMatch({
                semanticType: inputs[curr],
                upstreamNodes
              })
            }
          }
        } catch {
          return { ...acc, missing: [...acc.missing, curr] }
        }
      },
      { resolved: {} as Record<Inputs, string>, missing: [] }
    )

    if (missing.length > 0) {
      throw new MissingUpstreamDataError(missing)
    }

    return resolved
  }
